import { syncRefs } from '@vueuse/core';
import {
  useIsFormDirty,
  useIsFormTouched,
  useIsFormValid,
  useIsSubmitting,
  useIsValidating,
  type ValidationOptions,
} from 'vee-validate';
import {
  inject,
  onBeforeUnmount,
  onUnmounted,
  provide,
  ref,
  toRef,
  type MaybeRef,
  type Ref,
} from 'vue';

type RefProperties<T> = {
  [key in keyof T]: Ref<T[key]>;
};

type MaybeRefProperties<T> = {
  [key in keyof T]: MaybeRef<T[key]>;
};

export interface FormStatus {
  hasForm: boolean;
  isFormRequired: boolean;
  isFormValid: boolean;
  isFormDirty: boolean;
  isFormTouched: boolean;
  isFormValidating: boolean;
  isFormSubmitting: boolean;
  submit: undefined | (() => boolean | Promise<boolean>);
  reset: undefined | (() => void | Promise<void>);
  validate: undefined | ((opts?: Partial<ValidationOptions> | undefined) => void | Promise<void>);
}

export interface SyncFormContextOptions {
  submitForm: () => boolean | Promise<boolean>;
  resetForm?: () => void | Promise<void>;
  validateForm?: (opts?: Partial<ValidationOptions> | undefined) => void | Promise<void>;
  required?: boolean;
}

export type FormStatusContext = RefProperties<FormStatus>;
export type FormStatusSync = MaybeRefProperties<Omit<FormStatus, 'isFormRequired' | 'hasForm'>>;

export const provideFormContext = () => {
  provide<FormStatusContext>('formContext', {
    hasForm: ref(false),
    isFormRequired: ref(false),
    isFormValid: ref(true),
    isFormDirty: ref(false),
    isFormTouched: ref(false),
    isFormValidating: ref(false),
    isFormSubmitting: ref(false),
    submit: ref(undefined),
    reset: ref(undefined),
    validate: ref(undefined),
  });
};

export const useFormContext = (syncStatus?: SyncFormContextOptions) => {
  const formStatus = inject<FormStatusContext>('formContext');

  if (!formStatus) {
    throw new Error('useFormStatus must be used within a provideFormStatus context');
  }

  const reset = () => {
    if (!formStatus) return;

    formStatus.hasForm.value = false;
    formStatus.isFormRequired.value = false;
    formStatus.isFormValid.value = true;
    formStatus.isFormDirty.value = false;
    formStatus.isFormTouched.value = false;
    formStatus.isFormValidating.value = false;
    formStatus.isFormSubmitting.value = false;
  };

  if (syncStatus) {
    formStatus.hasForm.value = true;

    syncRefs(toRef(syncStatus, 'required'), formStatus.isFormRequired, { immediate: true });
    syncRefs(toRef(syncStatus, 'submitForm'), formStatus.submit, { immediate: true });
    syncRefs(toRef(syncStatus, 'resetForm'), formStatus.reset, { immediate: true });
    syncRefs(toRef(syncStatus, 'validateForm'), formStatus.validate, { immediate: true });
    syncRefs(useIsFormDirty(), formStatus.isFormDirty, { immediate: true });
    syncRefs(useIsFormValid(), formStatus.isFormValid, { immediate: true });
    syncRefs(useIsSubmitting(), formStatus.isFormSubmitting, { immediate: true });
    syncRefs(useIsFormTouched(), formStatus.isFormTouched, { immediate: true });
    syncRefs(useIsValidating(), formStatus.isFormValidating, { immediate: true });

    onBeforeUnmount(reset);
  }

  return formStatus;
};
